import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dialogTitleClasses from '@mui/material/DialogTitle/dialogTitleClasses';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 12,
    minWidth: 400,
    maxWidth: 500,
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  // Override MUI: DialogTitle + DialogContent uses paddingTop: 0 by default.
  [`.${dialogTitleClasses.root} + &`]: {
    paddingTop: theme.spacing(3),
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px',
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: 12,
}));

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  showCloseButton = true,
  showCancelButton = true,
  maxWidth = 'sm',
  isPassive = false,
  /** Match Carbon preventCloseOnClickOutside: ignore backdrop (and escape) so only buttons / X close */
  disableBackdropClose = false,
}) => {
  const handleDialogClose = (event, reason) => {
    if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    onClose(event);
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={maxWidth}
      fullWidth
    >
      <StyledDialogTitle>
        <Box>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        </Box>
        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={e => onClose(e)}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </StyledDialogTitle>

      <StyledDialogContent>
        {children}
      </StyledDialogContent>

      {!isPassive && (
        <StyledDialogActions>
          {showCancelButton && (
            <Button
              onClick={e => onClose(e)}
              variant="outlined"
              sx={{
                borderColor: 'text.secondary',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {cancelText}
            </Button>
          )}
          <Button
            onClick={onConfirm}
            variant="contained"
            color={confirmColor}
          >
            {confirmText}
          </Button>
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmColor: PropTypes.oneOf(['primary', 'secondary', 'error', 'warning', 'info', 'success']),
  showCloseButton: PropTypes.bool,
  showCancelButton: PropTypes.bool,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  isPassive: PropTypes.bool,
  disableBackdropClose: PropTypes.bool,
};

export default ConfirmDialog;
